import React from 'react';

/**
 * Стрічка, що повільно їде сама.
 *
 * Анімація — чисті CSS-кейфрейми на transform (див. `.marquee-*` в index.css),
 * тобто вся робота лежить на композиторі й головний потік під час прокрутки
 * сторінки не будиться. Це той самий підхід, що й у фонових плям: попередня
 * реалізація такого ефекту на Framer Motion з'їдала кадри при скролі.
 *
 * Стрічка зупиняється, коли на неї навели мишу (щоб можна було прочитати й
 * натиснути), коли фокус клавіатури на одній з карток, коли вона поза
 * екраном і коли в системі вимкнено анімації. Під нею є кнопка паузи: рух,
 * що триває довше п'яти секунд, має зупинятися й без наведення (WCAG 2.2.2)
 * — з телефона навести мишу нема як. Стрічку також можна крутити пальцем:
 * під анімацією лишається звичайний горизонтальний скрол.
 *
 * Якщо картка одна, стрічка не їде взагалі. Дублювати єдину картку заради
 * руху означало б показувати відвідувачу той самий заголовок двічі поспіль —
 * це читається як помилка, а не як стрічка.
 */
export const PlaybackIcon = ({ paused, className = 'w-3 h-3' }) => (
  <svg viewBox="0 0 16 16" className={className} fill="currentColor" aria-hidden="true" focusable="false">
    {paused ? <path d="M4.5 2.8v10.4a.6.6 0 0 0 .9.5l8.2-5.2a.6.6 0 0 0 0-1L5.4 2.3a.6.6 0 0 0-.9.5Z" /> : <path d="M4 2.5h2.6v11H4zM9.4 2.5H12v11H9.4z" />}
  </svg>
);

/**
 * Кнопка паузи для стрічок (тут і в Ticker). Коли в системі вимкнено
 * анімації, стрічки й так стоять, і кнопка ховається.
 */
export const MarqueeToggle = ({ paused, onToggle, className = '' }) => {
  const label = paused ? 'Запустити стрічку' : 'Зупинити стрічку';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={label}
      className={`motion-reduce:hidden inline-flex items-center justify-center w-8 h-8 rounded-full border border-teal-200 bg-white/90 text-teal-800 hover:bg-white hover:border-teal-400 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 ${className}`}
    >
      <PlaybackIcon paused={paused} />
    </button>
  );
};

// Фокус усередині стрічки зупиняє її так само, як наведення: картка, на якій
// стоїть фокус, не має від'їжджати з-під нього. Клас чіпляється до треку, а
// не до обгортки: className обгортки змінює IntersectionObserver напряму.
const MARQUEE_FOCUS_PAUSE = 'group-focus-within:[animation-play-state:paused]';

// Другий набір карток — лише для безшовного циклу: для диктора його немає
// (aria-hidden), а Tab не має заходити в посилання, яких «насправді» немає.
// inert тут не годиться: він вимикає й клік, а другий набір половину часу
// стоїть на екрані, і його картки мають відкриватися мишею.
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

// `tone` — колір секції, у який згасають краї стрічки: на сірому тлі білий
// туман по краях читався б як дві світлі смуги.
const AutoScroller = ({ items, renderItem, itemKey, seconds, tone = 'white' }) => {
  const wrapRef = React.useRef(null);
  const copyRef = React.useRef(null);
  const [paused, setPaused] = React.useState(false);
  const enabled = items.length > 1;

  React.useEffect(() => {
    const copy = copyRef.current;
    if (!copy) return;
    copy.querySelectorAll(FOCUSABLE).forEach((node) => node.setAttribute('tabindex', '-1'));
  }, [enabled, items]);

  // Пауза, поки стрічка поза екраном: анімація, якої ніхто не бачить, — це
  // просто робота відеокарти в порожнечу (і батарея на телефоні).
  React.useEffect(() => {
    const node = wrapRef.current;
    if (!node || !enabled || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      node.classList.toggle('marquee-paused', !entry.isIntersecting);
    }, { rootMargin: '100px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled]);

  if (!enabled) {
    return <div className="grid grid-cols-1">{items.map((item) => renderItem(item))}</div>;
  }

  // Довжина циклу від кількості карток, щоб швидкість руху не залежала від
  // того, скільки їх зараз: інакше кожна нова стаття прискорювала б стрічку.
  const duration = seconds || items.length * 9;

  return (
    <div>
      <div ref={wrapRef} className="marquee group relative -mx-5 px-5 overflow-hidden">
        {/* Краї згасають у білий, щоб картки не обрубувалися на межі екрана. */}
        <div className={`pointer-events-none absolute inset-y-0 left-0 w-10 z-10 bg-gradient-to-r to-transparent ${tone === 'muted' ? 'from-slate-50' : 'from-white'}`}></div>
        <div className={`pointer-events-none absolute inset-y-0 right-0 w-10 z-10 bg-gradient-to-l to-transparent ${tone === 'muted' ? 'from-slate-50' : 'from-white'}`}></div>

        <div className="overflow-x-auto no-scrollbar">
          <div
            className={`marquee-track flex w-max gap-5 lg:gap-6 ${MARQUEE_FOCUS_PAUSE}`}
            style={{ '--marquee-duration': `${duration}s`, animationPlayState: paused ? 'paused' : undefined }}
          >
            {/* Два однакові набори підряд: коли перший доїжджає до кінця, на
                екрані вже стоїть другий, і повернення на початок непомітне. */}
            {[0, 1].map((copy) => (
              <div
                key={copy}
                ref={copy === 1 ? copyRef : undefined}
                className="flex gap-5 lg:gap-6"
                aria-hidden={copy === 1 || undefined}
              >
                {items.map((item) => (
                  <div key={`${copy}-${itemKey(item)}`} className="w-[19rem] sm:w-[23rem] flex-shrink-0">
                    {renderItem(item)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Поза обгорткою: там кнопку накрив би туман правого краю. */}
      <div className="mt-3 flex justify-end motion-reduce:hidden">
        <MarqueeToggle paused={paused} onToggle={() => setPaused((p) => !p)} />
      </div>
    </div>
  );
};

export default AutoScroller;
